import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { canRefreshAdminSession, refreshAdminSession } from '../lib/token-refresh';
import { useAuthStore } from '../stores/auth-store';

// Resolve the socket.io endpoint with no per-environment config:
//   - `VITE_WS_URL` wins if set (explicit override, e.g. a remote API host).
//   - Local dev: the SPA runs on the vite dev server (localhost:3001) while the
//     API server listens on :3000, so connect there directly (CORS-allowed).
//   - Deployed (waypoint-dev / waypoint / …): connect SAME-ORIGIN — an https
//     page upgrades to wss on its own origin, which the API's reverse proxy
//     routes to socket.io. This is why it "works locally but not deployed":
//     the old hardcoded `http://localhost:3000` fallback pointed deployed
//     pages at the visitor's own machine.
function resolveWsUrl(): string {
  const envUrl = (import.meta as unknown as Record<string, Record<string, string>>)
    .env?.VITE_WS_URL;
  if (envUrl) return envUrl;
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:3000';
  return window.location.origin;
}

const WS_URL = resolveWsUrl();

let socket: Socket | null = null;
// ATL-001144: a refused handshake renews the token at most once until a
// connection succeeds, so a server that keeps refusing (every refresh yields a
// new token) cannot turn into a refresh loop.
let renewedSinceConnect = false;
let unsubscribeToken: (() => void) | null = null;
let refCount = 0;

function getSocket(): Socket {
  if (!socket) {
    // ATL-001144: access tokens live 15 minutes, so the token is read on every
    // (re)connect rather than captured once — a reconnect after a network
    // blip or laptop sleep must present the current token, not the login one.
    socket = io(WS_URL, {
      auth: (cb) => cb({ token: localStorage.getItem('accessToken') }),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    socket.on('connect', () => {
      renewedSinceConnect = false;
      console.log('[WS] Connected:', socket?.id);
    });

    socket.on('disconnect', (reason) => {
      console.log('[WS] Disconnected:', reason);
    });

    socket.on('connect_error', (err) => {
      console.warn('[WS] Connection error:', err.message);
      // The server refused the handshake (token expired, or revoked by a
      // logout on another device). socket.io does not retry a refusal on its
      // own (`active` is false), so renew the token once and reconnect. If
      // renewal fails the socket stays down until the token changes (below).
      const s = socket;
      const token = localStorage.getItem('accessToken');
      if (s && !s.active && token && !renewedSinceConnect && canRefreshAdminSession()) {
        renewedSinceConnect = true;
        void refreshAdminSession(token).then((outcome) => {
          if (outcome.status === 'renewed' && socket === s && !s.connected) s.connect();
        });
      }
    });

    // A socket the server refused stays down; bring it back as soon as the
    // session has a new access token (renewed elsewhere, org switch, re-login).
    unsubscribeToken = useAuthStore.subscribe((state, prev) => {
      const s = socket;
      if (state.accessToken && state.accessToken !== prev.accessToken && s && !s.connected && !s.active) {
        s.connect();
      }
    });
  }
  return socket;
}

function releaseSocket() {
  if (socket && refCount <= 0) {
    unsubscribeToken?.();
    unsubscribeToken = null;
    socket.disconnect();
    socket = null;
    refCount = 0;
    renewedSinceConnect = false;
  }
}

/**
 * Hook that manages the shared Socket.io connection.
 * Connects on mount (first consumer), disconnects when last consumer unmounts.
 */
export function useWebSocket() {
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    refCount++;
    socketRef.current = getSocket();

    return () => {
      refCount--;
      if (refCount <= 0) {
        releaseSocket();
      }
    };
  }, []);

  const emit = useCallback((event: string, data?: unknown) => {
    socketRef.current?.emit(event, data);
  }, []);

  const subscribe = useCallback((event: string, handler: (data: unknown) => void) => {
    socketRef.current?.on(event, handler);
    return () => {
      socketRef.current?.off(event, handler);
    };
  }, []);

  return { socket: socketRef.current, emit, subscribe };
}

/**
 * Hook that subscribes to a specific WebSocket event.
 * Automatically unsubscribes on cleanup.
 */
export function useWebSocketEvent(event: string, handler: (data: unknown) => void) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const s = getSocket();
    refCount++;

    const wrappedHandler = (data: unknown) => {
      handlerRef.current(data);
    };

    s.on(event, wrappedHandler);

    return () => {
      s.off(event, wrappedHandler);
      refCount--;
      if (refCount <= 0) {
        releaseSocket();
      }
    };
  }, [event]);
}
